import { Add } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, FedExTrackingNumber, GreenText, OrderableTableCell, PastDueCell, Title } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getNeedsNewTitlesOCRdToDoList } from 'controllers/toDoLists/ohioBuildABoxes'
import { VehicleDto } from 'dtos'
import { NeedsNewTitlesOCRdDto, NeedsNewTitlesOCRdQueryParams } from 'dtos/toDoLists/ohioBuildABoxes'
import { ReadyForOutboundTripDto } from 'dtos/toDoLists/outboundTrips'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'

export default function NeedsNewTitlesOCRdToDoList() {
  const { setIsLayoutLoading } = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<NeedsNewTitlesOCRdQueryParams>({
    page: 0,
    pageSize: 10,
    order: 'asc',
    orderBy: 'shippedToOhioDateTime'
  })
  const [toDoList, setToDoList] = useState<NeedsNewTitlesOCRdDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: NeedsNewTitlesOCRdQueryParams) => {
    setIsLayoutLoading(true)
    getNeedsNewTitlesOCRdToDoList(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => setIsLayoutLoading(false))
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Title>Needs New Titles OCR'd</Title>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='owner'>Owner</MenuItem>

                    <MenuItem value='last6OfVIN'>Last 6 Of VIN</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'owner', 'last6OfVIN', 'importerStockNumber', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={!filterField || !filterValue}
                    variant='contained'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        [filterField]: filterValue
                      })

                      setFilterField('')
                      setFilterValue('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        queryParams.owner ||
                          queryParams.last6OfVIN ||
                          queryParams.importerStockNumber ||
                          queryParams.year ||
                          queryParams.make ||
                          queryParams.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        owner: undefined,
                        last6OfVIN: undefined,
                        importerStockNumber: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.owner && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Owner contains "${queryParams.owner}"`}
                    onDelete={() => setQueryParams({ ...queryParams, owner: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.last6OfVIN && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Last 6 Of VIN contains "${queryParams.last6OfVIN}"`}
                    onDelete={() => setQueryParams({ ...queryParams, last6OfVIN: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${queryParams.year}`}
                    onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${queryParams.make}"`}
                    onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${queryParams.model}"`}
                    onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <OrderableTableCell<NeedsNewTitlesOCRdDto>
                              keyName='shippedToBWIDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsNewTitlesOCRdDto | undefined}
                            >
                              Shipped To BWI
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsNewTitlesOCRdDto>
                              keyName='receivedByBWIDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsNewTitlesOCRdDto | undefined}
                            >
                              Received By BWI
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsNewTitlesOCRdDto>
                              keyName='trackingNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsNewTitlesOCRdDto | undefined}
                            >
                              Tracking Number
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsNewTitlesOCRdDto>
                              keyName='owner'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsNewTitlesOCRdDto | undefined}
                            >
                              Owner
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsNewTitlesOCRdDto>
                              keyName='last6OfVIN'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsNewTitlesOCRdDto | undefined}
                            >
                              Last 6 Of VIN
                            </OrderableTableCell>

                            <OrderableTableCell<NeedsNewTitlesOCRdDto>
                              keyName='importerStockNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof NeedsNewTitlesOCRdDto | undefined}
                            >
                              Importer Stock #
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='year'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Year
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='make'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Make
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='model'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Model
                            </OrderableTableCell>

                            <TableCell padding='checkbox'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {toDoList.map((item, index) => (
                            <TableRow key={item.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <PastDueCell daysDiff={2}>{item.shippedToBWIDateTime}</PastDueCell>

                              <PastDueCell daysDiff={1}>{item.receivedByBWIDateTime}</PastDueCell>

                              <TableCell>
                                <FedExTrackingNumber>{item.trackingNumber}</FedExTrackingNumber>
                              </TableCell>

                              <TableCell>{item.owner}</TableCell>

                              <TableCell>{item.last6OfVIN}</TableCell>

                              <TableCell>{item.importerStockNumber}</TableCell>

                              <TableCell>{item.year}</TableCell>

                              <TableCell>{item.make}</TableCell>

                              <TableCell>{item.model}</TableCell>

                              <TableCell padding='checkbox'>
                                <Button onClick={() => navigate(`/vehicles/${item.vehicleGuid}/title`)} size='small' variant='text'>
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={queryParams.pageSize}
                              page={queryParams.page}
                              onPageChange={(_, page) => {
                                setQueryParams({
                                  ...queryParams,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setQueryParams({
                                  ...queryParams,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Needs New Titles OCR’d To Do List:**

Vehicles appear in this list under the following condition:

The FedEx API indicates that the return package (associated with the “Build A Box” return label) has been shipped to BWI.

The vehicle record does not have an associated Ohio Title document.

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Owner
   - Last 6 Of VIN
   - Importer Stock #
   - Year
   - Make
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Owner: Displays a free-type filter field.
   - Last 6 Of VIN: Displays a free-type filter field.
   - Importer Stock #: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Shipped To BWI Column:**

Displays the Shipped To BWI date retrieved from the FedEx API.

This date is bold and red if the current date is ≥ “Shipped To BWI” date + 2 days.

"Shipped To BWI" indicates the return package has been dropped off at FedEx, and FedEx confirms possession via their API.

**Received By BWI Column:**

Displays the Received By BWI date retrieved from the FedEx API.

This date is bold and red if the current date is ≥ “Received By BWI” date + 1 day.

**Tracking # Column:**

Displays the Tracking # from Build A Box.

This is the FedEx tracking number for the return label (used for new titles being sent from the Ohio BMV to BWI).

The tracking number is a clickable link that opens the FedEx website for tracking details.

**Owner Column:**

Displays the owner’s name from the “Owner Name” field under Vehicle Details > Titles tab.

**Last 6 Of VIN Column:**

Displays the last six characters of the vehicle’s VIN.

**Importer Stock #:**

Displays a sequential number starting at 20000, followed by a three-character Exporter Code or Buyer Code (if the vehicle has a buyer).

This field is populated only when the vehicle is locked.

**Year Column:**

Displays the vehicle’s year from Vehicle Details.

**Make Column:**

Displays the vehicle’s make from Vehicle Details.

**Model Column:**

Displays the vehicle’s model from Vehicle Details.

**Edit Action:**

Pressing “Edit” in a vehicle's row navigates to that vehicle’s Vehicle Details > Titles tab.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
